import clsx from 'clsx';
import { useState, useMemo } from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from 'react-query';
import { useRouter } from 'next/router';
import { AssetIcon, Icon, StackView, Text } from '@cronos-labs/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk';
import ArrowUpSVG from '@public/assets/images/arrow-up.svg';
import ArrowDownSVG from '@public/assets/images/arrow-down.svg';

import { formatPercent } from '@lib/units';
import useIsMobile from '@hooks/useIsMobile';
import useSupplyApy from '@hooks/useSupplyApy';
import useBorrowApy from '@hooks/useBorrowApy';
import useVaultDeposits from '@hooks/useVaultDeposits';
import { BoostType } from '@hooks/useGetXTonicBoostDailyDistributeRate';
import Card from '@components/Card/Card';
import getAssetLogoUri from '@components/SupportedAssetIcon/getAssetLogoUri';
import TransactionModalsProvider, {
  TransactionModalsContext,
} from '@providers/TransactionModalsProvider/TransactionModalsProvider';

import {
  useGetAssetDetails,
  useGetBoostingMultiplier,
  useXTonicBoostedAPY,
} from './XTonicBoostingHooks';

const MarketBoosted = ({
  className,
  tTokenAddress,
  boostType,
}: {
  className?: string;
  tTokenAddress: TectonicAsset['tTokenAddress'];
  boostType: BoostType;
}) => {
  const data = useGetAssetDetails(tTokenAddress);

  const { tonicSupplyApy } = useSupplyApy(
    data?.pool ?? 'MAIN',
    data?.boostedAsset ?? null,
    {
      skip: boostType !== BoostType.SUPPLY,
    }
  );
  const { tonicBorrowApy } = useBorrowApy(
    data?.pool ?? 'MAIN',
    data?.boostedAsset ?? null,
    {
      skip: boostType !== BoostType.BORROW,
    }
  );
  const boostAPY = useXTonicBoostedAPY(tTokenAddress, boostType, data?.pool);
  return (
    <div className="ml-5 mt-1 flex items-center justify-between mobile:ml-1">
      <div className="flex items-center">
        {data?.boostedAsset?.symbol && (
          <AssetIcon
            name={data?.boostedAsset?.symbol}
            src={getAssetLogoUri(data?.boostedAsset?.symbol) ?? ''}
          />
        )}
        <Text className="ml-1">{data?.boostedAsset?.symbol ?? ''}</Text>
        <div className="ml-1 w-[80px]">
          <Text className={clsx(className, 'w-[120%] capitalize')}>
            {data?.pool.toLowerCase()} Pool {BoostType[boostType].toLowerCase()}
          </Text>
        </div>
      </div>
      <div className="ml-2 flex items-center">
        <Icon.Boosted />
        <Text className="bg-gradient-to-r from-vaultChart6Months to-highlightBorderColorTo bg-clip-text text-transparent">
          TONIC APY{' '}
          {boostType === BoostType.BORROW
            ? formatPercent((tonicBorrowApy + boostAPY) * 100)
            : formatPercent((tonicSupplyApy + boostAPY) * 100)}
        </Text>
      </div>
    </div>
  );
};

interface XTonicBoostedProps {
  boostedSupplyCollections?: string[];
  boostedBorrowCollections?: string[];
  refetchBoostedSupplyCollections: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<string[], unknown>>;
  refetchBoostedBorrowCollections: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<string[], unknown>>;
}

const XTonicBoosted = ({
  boostedSupplyCollections,
  boostedBorrowCollections,
  refetchBoostedSupplyCollections,
  refetchBoostedBorrowCollections,
}: XTonicBoostedProps) => {
  const [isCollapse, setCollapse] = useState<boolean>(true);

  const { push } = useRouter();
  const isMobile = useIsMobile();
  const { data } = useVaultDeposits();
  const xTonicLocked = data?.some((deposit) => deposit.amount.gt(0));
  const { data: multiplier } = useGetBoostingMultiplier();
  const handleLock = () => push('/vaults');

  const totalBoostedMarkets = useMemo(() => {
    if (boostedSupplyCollections && boostedBorrowCollections) {
      return (
        boostedSupplyCollections?.length + boostedBorrowCollections?.length
      );
    }
  }, [boostedSupplyCollections, boostedBorrowCollections]);

  return (
    <TransactionModalsProvider
      onTxSuccess={(): void => {
        refetchBoostedSupplyCollections();
        refetchBoostedBorrowCollections();
      }}
    >
      <TransactionModalsContext.Consumer>
        {({ onOpenModal }) => (
          <div className="mt-2 bg-gradient-to-r from-highlightBorderColorFrom via-rainbowBlue via-rainbowPink to-highlightBorderColorTo p-[1px] mobile:rounded-[5px]">
            <Card padding="small" className="mobile:rounded-[5px]">
              <div className="flex w-full justify-between bg-surface mobile:flex-col">
                <StackView direction="vertical" className="w-full">
                  <div className="flex items-center justify-between">
                    {' '}
                    <div className="flex items-center">
                      {' '}
                      <Icon.Boost className="text-yellowPrimary" />
                      <Text
                        className="ml-2 text-yellowPrimary"
                        variant="heading3"
                      >
                        {'Multiplier activated'}
                      </Text>
                    </div>
                    <div
                      onClick={() => setCollapse((prev) => !prev)}
                      className="cursor-pointer"
                    >
                      {isCollapse ? (
                        <ArrowDownSVG fill={'yellow'} stroke={'yellow'} />
                      ) : (
                        <ArrowUpSVG fill={'yellow'} stroke={'yellow'} />
                      )}
                    </div>
                  </div>
                  {!isCollapse && (
                    <>
                      {' '}
                      <Text variant="default" className="mt-1">
                        Earn higher interest or pay lower borrowing fees by
                        locking xTONIC in a vault.
                      </Text>
                      <a
                        className="mt-3 !bg-surface text-yellow"
                        href="https://tectonic.gitbook.io/docs/protocol/tonic-rewards-boost"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Learn more
                      </a>
                    </>
                  )}
                </StackView>
              </div>

              {!isCollapse && (
                <div className="mt-1 w-fit rounded-md border-1 border-greyBorder bg-blueElevatedSurface">
                  <Text className="p-1 text-greyBorder">
                    You will stop receiving boost rewards if you fully close out
                    an existing position. Select another market to boost
                    instead.
                  </Text>
                </div>
              )}

              <div className="flex mobile:flex-col">
                {multiplier && (
                  <div className="flex flex-row">
                    <Text className="mt-3 mobile:mt-2">
                      Boost Multiplier applied:{' '}
                    </Text>
                    <Text className="mt-3  ml-1 text-vaultChart12Months mobile:mt-2">
                      {Number(multiplier) ?? ''}x
                    </Text>
                  </div>
                )}
                {isCollapse && (
                  <div className="flex desktop:flex-row mobile:flex-col">
                    <Text className="mt-3 desktop:ml-2 mobile:mt-2">
                      {totalBoostedMarkets
                        ? `${totalBoostedMarkets} markets boosted`
                        : ''}
                    </Text>
                    {boostedSupplyCollections?.map((tTokenAddress, key) => (
                      <MarketBoosted
                        className="text-vaultChart6Months"
                        tTokenAddress={tTokenAddress}
                        boostType={BoostType.SUPPLY}
                        key={key}
                      />
                    ))}
                    {boostedBorrowCollections?.map((tTokenAddress, key) => (
                      <MarketBoosted
                        className="text-rainbowPinkSecondary"
                        tTokenAddress={tTokenAddress}
                        boostType={BoostType.BORROW}
                        key={key}
                      />
                    ))}
                  </div>
                )}
              </div>

              {isMobile && !isCollapse && (
                <div className="relative">
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline">
                      <div className="mt-1 h-3 w-3 rounded-full bg-semiTransparent bg-completed text-center leading-6 text-darkerBlue" />

                      <Text className="ml-1">Lock xTonic in a vault</Text>
                    </div>
                    <div
                      className="cursor-pointer font-medium text-yellow"
                      onClick={handleLock}
                    >
                      Lock
                    </div>
                  </div>
                  <Text
                    className={clsx('absolute ml-4', {
                      hidden: !xTonicLocked,
                    })}
                  >
                    xTonic locked
                  </Text>
                  <Card
                    className="absolute mt-4 ml-4 !w-11/12 border-0 bg-blueElevatedSurface p-1"
                    padding="small"
                  >
                    <div className="flex items-center justify-between">
                      <Icon.Increment className="text-rainbowRed" />
                      <Text className="ml-0.5">
                        Stake NFT for higher APY (Optional)
                      </Text>
                      <div
                        className={clsx(
                          'cursor-pointer font-medium',
                          xTonicLocked ? 'text-yellow' : 'text-greyBorder'
                        )}
                        onClick={handleLock}
                      >
                        Stake
                      </div>
                    </div>
                    {/* <Text className="ml-2">2 of 5 NFTs staked</Text> */}
                  </Card>
                  <div className="ml-1.5 mt-1 inline-block h-[120px] min-h-[1em] w-[1px] bg-greyBorder" />
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline">
                      <div className="mt-1 h-3 w-3 rounded-full bg-semiTransparent bg-completed text-center leading-6  text-darkerBlue" />

                      <Text className="ml-1">Boost up to 2 markets</Text>
                    </div>
                    <div
                      className="cursor-pointer font-medium text-yellow"
                      onClick={() => onOpenModal(null, 'updateBoost')}
                    >
                      Edit
                    </div>
                  </div>
                  {boostedSupplyCollections?.map((tTokenAddress, key) => (
                    <MarketBoosted
                      className="text-vaultChart6Months"
                      tTokenAddress={tTokenAddress}
                      boostType={BoostType.SUPPLY}
                      key={key}
                    />
                  ))}
                  {boostedBorrowCollections?.map((tTokenAddress, key) => (
                    <MarketBoosted
                      className="text-rainbowPinkSecondary"
                      tTokenAddress={tTokenAddress}
                      boostType={BoostType.BORROW}
                      key={key}
                    />
                  ))}
                </div>
              )}

              {!isMobile && !isCollapse && (
                <>
                  <div className="m-auto flex w-2/4">
                    <div
                      className={clsx(
                        'h-3 w-3 rounded-full bg-semiTransparent text-center leading-6 text-darkerBlue',
                        { 'bg-completed': xTonicLocked }
                      )}
                    />

                    <div className="h-px my-1.5 ml-0.5 w-full border-1 border-greyBorder bg-greyBorder" />
                    <div
                      className={clsx(
                        'ml-1 h-3 w-3 rounded-full bg-semiTransparent text-center leading-6 text-darkerBlue',
                        { 'bg-completed': xTonicLocked }
                      )}
                    />
                  </div>
                  <div className="mt-2 flex justify-around">
                    <div className="flex">
                      <Text>Lock xTonic in a vault</Text>
                      <div
                        className="ml-2 cursor-pointer font-medium text-yellow"
                        onClick={handleLock}
                      >
                        Lock
                      </div>
                    </div>
                    <div className="flex">
                      <Text>Boost up to 2 markets</Text>
                      <div
                        className="ml-2 cursor-pointer font-medium text-yellow"
                        onClick={() => onOpenModal(null, 'updateBoost')}
                      >
                        Edit
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-around">
                    <div>
                      <Text
                        className={clsx('text-center', {
                          hidden: !xTonicLocked,
                        })}
                      >
                        {' '}
                        xTONIC locked
                      </Text>
                      <Card
                        className="mt-1.5 border-0 bg-blueElevatedSurface p-1"
                        padding="small"
                      >
                        <div className="flex items-center justify-between">
                          <Icon.Increment className="text-rainbowRed" />
                          <Text className="ml-1">
                            Stake NFT for higher APY (Optional)
                          </Text>
                          <div
                            className="ml-2 cursor-pointer font-medium text-yellow"
                            onClick={handleLock}
                          >
                            Stake
                          </div>
                        </div>
                        {/* <Text className="ml-3">2 of 5 NFTs staked</Text> */}
                      </Card>
                    </div>
                    <div className="w-1/3">
                      {boostedSupplyCollections?.map((tTokenAddress, key) => (
                        <MarketBoosted
                          className="text-vaultChart6Months"
                          tTokenAddress={tTokenAddress}
                          boostType={BoostType.SUPPLY}
                          key={key}
                        />
                      ))}
                      {boostedBorrowCollections?.map((tTokenAddress, key) => (
                        <MarketBoosted
                          className="text-rainbowPinkSecondary"
                          tTokenAddress={tTokenAddress}
                          boostType={BoostType.BORROW}
                          key={key}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </TransactionModalsContext.Consumer>
    </TransactionModalsProvider>
  );
};

export default XTonicBoosted;
